import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

interface CollapsibleSectionProps {
  title: string;
  subtitle?: React.ReactNode;
  defaultOpen?: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Shared collapsible section wrapper for the OPS Review Workspace — content
 * mounts immediately (unlike FindingAccordionItem's lazy-mount) since every
 * section here already has its own independently-fetching query, so mounting
 * collapsed just hides the rendered DOM rather than deferring the fetch.
 */
export function CollapsibleSection({
  title,
  subtitle,
  defaultOpen = true,
  actions,
  children,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
          aria-expanded={isOpen}
        >
          <FontAwesomeIcon
            icon={isOpen ? faChevronDown : faChevronRight}
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
          />
          <span className="truncate text-sm font-semibold text-foreground">{title}</span>
          {subtitle && <span className="shrink-0 text-xs text-muted-foreground">{subtitle}</span>}
        </button>
        {actions && (
          <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </div>
      {isOpen && <div className="px-5 py-4">{children}</div>}
    </div>
  );
}
